import { useMemo, useState } from 'react';
import { Trophy, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// Cricket-team-inspired palette (gradients)
const PALETTE = [
  { from: '#FF7F00', to: '#E85D04', text: '#FFFFFF' }, // MPL orange
  { from: '#0E7C66', to: '#0a5d4d', text: '#FFFFFF' }, // pitch green
  { from: '#9B1B30', to: '#7a1426', text: '#FFFFFF' }, // crimson
  { from: '#3D348B', to: '#2b2566', text: '#FFFFFF' }, // royal purple
  { from: '#1B4965', to: '#0f2e44', text: '#FFFFFF' }, // ocean
  { from: '#D7263D', to: '#a51c2e', text: '#FFFFFF' }, // bright red
  { from: '#0B6E4F', to: '#08523b', text: '#FFFFFF' }, // forest
];

const DAY_MS = 86400000;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

const startOfMonth = (d) => {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfMonth = (d) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
};
const addMonths = (d, n) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
};
const monthLabel = (d) =>
  d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });

const PRESETS = [
  { label: '1 mo', months: 1 },
  { label: '3 mo', months: 3 },
  { label: '6 mo', months: 6 },
  { label: '12 mo', months: 12 },
  { label: '24 mo', months: 24 },
];

const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 48;
const LEFT_LABEL_WIDTH = 200;

const TournamentCalendar = ({ tournaments = [] }) => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const dated = useMemo(
    () =>
      tournaments
        .filter((t) => t.start_date && t.end_date)
        .map((t, idx) => ({
          ...t,
          _start: new Date(t.start_date),
          _end: new Date(t.end_date),
          _palette: PALETTE[idx % PALETTE.length],
        }))
        .sort((a, b) => a._start - b._start),
    [tournaments]
  );

  // Default the visible window to span all scheduled tournaments + a small buffer
  const initialWindow = useMemo(() => {
    if (dated.length === 0) {
      return { start: startOfMonth(today), months: 6 };
    }
    const first = dated.reduce((a, b) => (a._start < b._start ? a : b));
    const last = dated.reduce((a, b) => (a._end > b._end ? a : b));
    const start = startOfMonth(first._start);
    const end = endOfMonth(last._end);
    const monthsSpan =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
    return { start, months: Math.max(3, monthsSpan) };
  }, [dated, today]);

  const [windowStart, setWindowStart] = useState(initialWindow.start);
  const [windowMonths, setWindowMonths] = useState(initialWindow.months);

  const windowEnd = useMemo(
    () => endOfMonth(addMonths(windowStart, windowMonths - 1)),
    [windowStart, windowMonths]
  );

  const totalDays = Math.max(
    1,
    Math.round((windowEnd - windowStart) / DAY_MS) + 1
  );

  // Build month tick marks for header
  const months = useMemo(() => {
    const arr = [];
    let cur = new Date(windowStart);
    while (cur <= windowEnd) {
      const m = startOfMonth(cur);
      const mEnd = endOfMonth(cur);
      const offsetDays = Math.round((m - windowStart) / DAY_MS);
      const widthDays =
        Math.round((Math.min(mEnd, windowEnd) - m) / DAY_MS) + 1;
      arr.push({
        key: `${m.getFullYear()}-${m.getMonth()}`,
        label: monthLabel(m),
        offsetPct: (offsetDays / totalDays) * 100,
        widthPct: (widthDays / totalDays) * 100,
      });
      cur = addMonths(cur, 1);
    }
    return arr;
  }, [windowStart, windowEnd, totalDays]);

  // Helper to compute bar position for a tournament inside the visible window
  const barFor = (t) => {
    const clampedStart = t._start < windowStart ? windowStart : t._start;
    const clampedEnd = t._end > windowEnd ? windowEnd : t._end;
    if (clampedEnd < windowStart || clampedStart > windowEnd) {
      return null; // out of window
    }
    const offsetDays = Math.round((clampedStart - windowStart) / DAY_MS);
    const widthDays = Math.max(1, Math.round((clampedEnd - clampedStart) / DAY_MS) + 1);
    return {
      leftPct: (offsetDays / totalDays) * 100,
      widthPct: (widthDays / totalDays) * 100,
      truncatedLeft: t._start < windowStart,
      truncatedRight: t._end > windowEnd,
    };
  };

  const todayPct = useMemo(() => {
    if (today < windowStart || today > windowEnd) return null;
    const offsetDays = Math.round((today - windowStart) / DAY_MS);
    return (offsetDays / totalDays) * 100;
  }, [today, windowStart, windowEnd, totalDays]);

  const liveCount = dated.filter((t) => t._start <= today && t._end >= today).length;

  const goPrev = () => setWindowStart(addMonths(windowStart, -1));
  const goNext = () => setWindowStart(addMonths(windowStart, 1));
  const goToday = () => {
    setWindowStart(addMonths(startOfMonth(today), -Math.floor(windowMonths / 2)));
  };

  if (dated.length === 0) {
    return (
      <Card data-testid="tournament-calendar-empty">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            Tournament Calendar
          </CardTitle>
          <CardDescription>
            Add a start &amp; end date to a tournament to see it on the Gantt timeline below.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center py-12">
          <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">
            No tournaments with dates yet. Edit a tournament and add its season window.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-testid="tournament-calendar"
      className="overflow-hidden border-border"
    >
      <CardHeader
        className="border-b"
        style={{
          background:
            'linear-gradient(135deg, #00204F 0%, #0b3a7a 60%, rgba(255,127,0,0.85) 140%)',
        }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="text-white">
            <CardTitle className="font-heading flex items-center gap-2 text-white">
              <Trophy className="h-5 w-5 text-[#FF7F00]" />
              Tournament Calendar
            </CardTitle>
            <CardDescription className="text-white/70 mt-1">
              {dated.length} scheduled · {liveCount > 0 ? `${liveCount} live now` : 'none live today'} ·
              Showing {monthLabel(windowStart)} → {monthLabel(addMonths(windowStart, windowMonths - 1))}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="flex items-center rounded-md bg-white/10 border border-white/20 p-0.5"
              data-testid="calendar-range-presets"
            >
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setWindowMonths(p.months)}
                  className={`text-xs px-2.5 py-1 rounded transition-colors ${
                    windowMonths === p.months
                      ? 'bg-[#FF7F00] text-white font-semibold'
                      : 'text-white/80 hover:bg-white/10'
                  }`}
                  data-testid={`calendar-range-${p.months}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={goPrev}
              className="text-white hover:bg-white/10 h-8 w-8 p-0"
              aria-label="Pan back"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={goToday}
              className="text-white hover:bg-white/10 h-8 px-2 text-xs"
              data-testid="calendar-today-button"
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={goNext}
              className="text-white hover:bg-white/10 h-8 w-8 p-0"
              aria-label="Pan forward"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Cricket pitch decorative band */}
        <div
          className="h-1 w-full"
          style={{
            background:
              'repeating-linear-gradient(90deg, #0E7C66 0 22px, #0a6657 22px 24px)',
          }}
        />

        <div className="overflow-x-auto">
          <div
            className="relative"
            style={{ minWidth: 880 }}
          >
            {/* Header: months strip */}
            <div className="flex border-b bg-muted/30">
              <div
                className="flex-shrink-0 px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground border-r"
                style={{ width: LEFT_LABEL_WIDTH }}
              >
                Tournament
              </div>
              <div
                className="relative"
                style={{ height: HEADER_HEIGHT, flex: 1 }}
              >
                {months.map((m) => (
                  <div
                    key={m.key}
                    className="absolute top-0 h-full flex items-center justify-center text-[11px] font-semibold text-muted-foreground border-r border-border/60"
                    style={{
                      left: `${m.offsetPct}%`,
                      width: `${m.widthPct}%`,
                    }}
                  >
                    {m.label}
                  </div>
                ))}
                {/* Today marker line in header */}
                {todayPct !== null && (
                  <div
                    className="absolute top-0 h-full"
                    style={{ left: `${todayPct}%` }}
                  >
                    <div
                      className="absolute top-1 -translate-x-1/2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shadow"
                      style={{ backgroundColor: '#FF7F00', color: 'white' }}
                    >
                      Today
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Body: rows */}
            <div className="relative">
              {/* Vertical month grid lines spanning all rows */}
              <div
                className="absolute inset-y-0 pointer-events-none"
                style={{ left: LEFT_LABEL_WIDTH, right: 0 }}
              >
                {months.map((m, i) =>
                  i === 0 ? null : (
                    <div
                      key={m.key}
                      className="absolute top-0 bottom-0 border-l border-border/40"
                      style={{ left: `${m.offsetPct}%` }}
                    />
                  )
                )}
                {/* Today vertical line */}
                {todayPct !== null && (
                  <div
                    className="absolute top-0 bottom-0 border-l-2 z-10"
                    style={{
                      left: `${todayPct}%`,
                      borderColor: '#FF7F00',
                      boxShadow: '0 0 6px rgba(255,127,0,0.45)',
                    }}
                  />
                )}
              </div>

              {dated.map((t) => {
                const bar = barFor(t);
                const isLive = t._start <= today && t._end >= today;
                const durationDays =
                  Math.round((t._end - t._start) / DAY_MS) + 1;

                return (
                  <div
                    key={t.tournament_id}
                    className="flex border-b border-border/50 hover:bg-muted/20 transition-colors"
                    style={{ height: ROW_HEIGHT }}
                    data-testid={`gantt-row-${t.tournament_id}`}
                  >
                    {/* Left label cell */}
                    <div
                      className="flex-shrink-0 flex items-center gap-2 px-4 border-r"
                      style={{ width: LEFT_LABEL_WIDTH }}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                        style={{
                          background: `linear-gradient(135deg, ${t._palette.from}, ${t._palette.to})`,
                        }}
                      />
                      <span className="text-sm font-medium truncate flex-1" title={t.name}>
                        {t.name}
                      </span>
                      {t.is_default && (
                        <Star className="h-3 w-3 text-[#FF7F00] flex-shrink-0" fill="#FF7F00" />
                      )}
                    </div>

                    {/* Bar track */}
                    <div className="relative flex-1">
                      {bar ? (
                        <div
                          className="absolute top-1/2 -translate-y-1/2 rounded-md flex items-center px-2 text-xs font-semibold shadow-sm overflow-hidden group cursor-default"
                          style={{
                            left: `${bar.leftPct}%`,
                            width: `${bar.widthPct}%`,
                            height: 26,
                            background: `linear-gradient(135deg, ${t._palette.from} 0%, ${t._palette.to} 100%)`,
                            color: t._palette.text,
                            borderTopLeftRadius: bar.truncatedLeft ? 0 : 6,
                            borderBottomLeftRadius: bar.truncatedLeft ? 0 : 6,
                            borderTopRightRadius: bar.truncatedRight ? 0 : 6,
                            borderBottomRightRadius: bar.truncatedRight ? 0 : 6,
                          }}
                          title={`${t.name}: ${fmtDate(t._start)} → ${fmtDate(t._end)} (${durationDays} days)`}
                        >
                          {/* Pitch-stripe texture overlay */}
                          <div
                            className="absolute inset-0 opacity-20 pointer-events-none"
                            style={{
                              background:
                                'repeating-linear-gradient(135deg, rgba(255,255,255,0.18) 0 6px, transparent 6px 12px)',
                            }}
                          />
                          <span className="relative truncate flex items-center gap-1.5">
                            {t.name}
                            {isLive && (
                              <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-white/25 px-1.5 py-0.5 rounded-sm">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                                Live
                              </span>
                            )}
                          </span>
                        </div>
                      ) : (
                        <div className="absolute top-1/2 -translate-y-1/2 left-2 text-[10px] italic text-muted-foreground/60">
                          Outside visible range
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend / detail strip */}
        <div className="px-5 py-4 border-t bg-muted/20">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Schedule details
          </div>
          <div className="flex flex-wrap gap-2">
            {dated.map((t) => {
              const isLive = t._start <= today && t._end >= today;
              const days = Math.round((t._end - t._start) / DAY_MS) + 1;
              return (
                <div
                  key={t.tournament_id}
                  className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-border bg-background"
                >
                  <span
                    className="h-3 w-3 rounded-sm flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${t._palette.from}, ${t._palette.to})`,
                    }}
                  />
                  <span className="font-semibold">{t.name}</span>
                  {t.is_default && (
                    <Badge variant="outline" className="h-4 px-1 py-0 text-[9px]">
                      DEFAULT
                    </Badge>
                  )}
                  {isLive && (
                    <Badge className="h-4 px-1 py-0 text-[9px] bg-emerald-600 text-white border-0 animate-pulse">
                      LIVE
                    </Badge>
                  )}
                  <span className="text-muted-foreground">
                    {fmtDate(t._start)} → {fmtDate(t._end)}
                  </span>
                  <span className="text-muted-foreground/70">({days}d)</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TournamentCalendar;
